import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { STRIPE_STARTER, STRIPE_PRO } from '@shared/config/stripe';
import {
  applyTemplateToSimpleDraft,
  buildMissioPipelinePreview,
  canUseCalcMissionTemplate,
  computeCalcMissionSimpleResult,
  createDefaultCalcSimpleDraft,
  getCalcMissionTemplates,
  runCalcScratchpad,
  type CalcMissionTier,
  type CalcMissionTemplate,
} from '@shared/domain';
import { readMissioInvoices } from '@shared/domain';
import { useActiveProjects, useCalcMissionSessions, useCurrentProject, useProfile, useWorkspaceStore } from '@shared/state';
import { CalcMissionSimpleDraft, Workspace } from '@shared/types';

function formatCurrency(value: number) {
  return `${Math.round(value).toLocaleString('fr-FR')} €`;
}

function formatCurrencyShort(value: number) {
  return `${Math.round(value).toLocaleString('fr-FR')} €`;
}

const MISSIO_STAGE_LABELS = {
  all: 'Toutes',
  prospect: 'À explorer',
  gagne: 'Cadrées',
  'en-cours': 'En cours',
  facture: 'Facturées',
} as const;

const MISSIO_INVOICE_LABELS = {
  all: 'Toutes',
  brouillon: 'Brouillons',
  envoyee: 'Envoyées',
  payee: 'Payées',
} as const;

function getMissioStageLabel(stage: keyof typeof MISSIO_STAGE_LABELS) {
  return MISSIO_STAGE_LABELS[stage];
}

function getMissioInvoiceLabel(status: keyof typeof MISSIO_INVOICE_LABELS) {
  return MISSIO_INVOICE_LABELS[status];
}

function tierTone(tier: CalcMissionTier) {
  if (tier === 'pro') return 'border-emerald-400/40 bg-emerald-300/10 text-emerald-100';
  if (tier === 'starter') return 'border-amber-400/40 bg-amber-300/10 text-amber-100';
  return 'border-slate-400/30 bg-slate-300/10 text-slate-100';
}

function tierLabel(tier: CalcMissionTier) {
  if (tier === 'pro') return 'Pro';
  if (tier === 'starter') return 'Starter';
  return 'Free';
}

function buildFallbackMissioPreview(profile: ReturnType<typeof useProfile>) {
  const tjm = profile.tjm || 0;
  const daysPerMonth = profile.daysPerMonth || 0;
  const missions = [
    {
      id: 'demo-prospect',
      clientNom: 'Studio Iris',
      titre: 'Refonte du portefeuille offres',
      notes: 'Aperçu limité du pipeline Missio.',
      budgetHT: 9600,
      tjm,
      joursEstimes: 12,
      statut: 'prospect' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'demo-encours',
      clientNom: 'Maison Lumen',
      titre: 'Pilotage mensuel',
      notes: 'Aperçu des repères de suivi.',
      budgetHT: 14400,
      tjm,
      joursEstimes: daysPerMonth,
      statut: 'en-cours' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: 'demo-facture',
      clientNom: 'Atelier Nova',
      titre: 'Mission cadrage',
      notes: 'Lecture de la partie facturation.',
      budgetHT: 7800,
      tjm,
      joursEstimes: 10,
      statut: 'facture' as const,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ];

  const grouped = {
    prospect: [missions[0]],
    gagne: [],
    'en-cours': [missions[1]],
    facture: [missions[2]],
  };

  return {
    missions,
    grouped,
    counts: {
      prospect: grouped.prospect.length,
      gagne: grouped.gagne.length,
      'en-cours': grouped['en-cours'].length,
      facture: grouped.facture.length,
    },
  };
}

export function CalcMissionPreviewDemo() {
  const profile = useProfile();
  const templates = useMemo(() => getCalcMissionTemplates(), []);
  const [tierFocus, setTierFocus] = useState<CalcMissionTier>('free');
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>('');
  const [simpleDraft, setSimpleDraft] = useState<CalcMissionSimpleDraft>(
    () => createDefaultCalcSimpleDraft(profile),
  );
  const [scratchpad, setScratchpad] = useState('');

  const counts = useMemo(() => ({
    free: templates.filter((template) => template.tier === 'free').length,
    starter: templates.filter((template) => template.tier === 'free' || template.tier === 'starter').length,
    pro: templates.length,
  }), [templates]);

  const accessibleTemplates = useMemo(
    () => templates.filter((template) => canUseCalcMissionTemplate(template, tierFocus)),
    [templates, tierFocus],
  );

  useEffect(() => {
    if (accessibleTemplates.some((template) => template.key === selectedTemplateKey)) return;
    setSelectedTemplateKey(accessibleTemplates[0]?.key || templates[0]?.key || '');
  }, [accessibleTemplates, selectedTemplateKey, templates]);

  const selectedTemplate = useMemo<CalcMissionTemplate | undefined>(() => {
    return templates.find((template) => template.key === selectedTemplateKey) || accessibleTemplates[0] || templates[0];
  }, [accessibleTemplates, selectedTemplateKey, templates]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const nextSimple = applyTemplateToSimpleDraft(createDefaultCalcSimpleDraft(profile), selectedTemplate, profile);
    setSimpleDraft({
      ...nextSimple,
      name: selectedTemplate.label,
      client: nextSimple.client || 'Client demo',
    });
    setScratchpad(selectedTemplate.content || '');
  }, [profile, selectedTemplate]);

  const simpleResult = useMemo(
    () => (selectedTemplate?.mode === 'simple' ? computeCalcMissionSimpleResult(simpleDraft) : null),
    [selectedTemplate?.mode, simpleDraft],
  );
  const scratchpadResult = useMemo(
    () => (selectedTemplate?.mode === 'scratchpad' ? runCalcScratchpad(scratchpad) : null),
    [scratchpad, selectedTemplate?.mode],
  );

  if (!selectedTemplate) {
    return (
      <div className="rounded-xl border border-border-strong bg-background-dark-secondary p-5">
        <p className="text-sm text-text-dark-secondary">Aucun template disponible pour l’aperçu.</p>
      </div>
    );
  }

  const tierSummary = tierFocus === 'free'
    ? 'Aperçu limité pour comprendre la structure.'
    : tierFocus === 'starter'
      ? 'Usage étendu avec plus de templates et de détails.'
      : 'Accès complet au catalogue et aux sessions avancées.';

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
        <div className="flex flex-wrap items-center gap-2">
          {(['free', 'starter', 'pro'] as const).map((tier) => (
            <button
              key={tier}
              type="button"
              onClick={() => setTierFocus(tier)}
              className={`rounded-full border px-3 py-2 text-xs uppercase tracking-[0.16em] transition-colors ${tierFocus === tier ? tierTone(tier) : 'border-border-strong bg-background-dark-tertiary text-text-dark-secondary hover:border-primary-500/30 hover:text-text-dark-primary'}`}
            >
              {tierLabel(tier)} · {counts[tier]}
            </button>
          ))}
        </div>

        <p className="mt-4 text-sm text-text-dark-secondary">{tierSummary}</p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
          {accessibleTemplates.map((template) => (
            <button
              key={template.key}
              type="button"
              onClick={() => setSelectedTemplateKey(template.key)}
              className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                selectedTemplate.key === template.key
                  ? 'border-primary-500/40 bg-primary-300/10 text-text-dark-primary'
                  : 'border-border-strong bg-background-dark-tertiary text-text-dark-secondary hover:border-primary-500/30 hover:text-text-dark-primary'
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-semibold">{template.label}</span>
                <span className={`rounded-full border px-2 py-1 text-[11px] uppercase tracking-[0.14em] ${tierTone(template.tier)}`}>
                  {tierLabel(template.tier)}
                </span>
              </div>
              <p className="mt-1 text-xs uppercase tracking-[0.14em] text-text-dark-tertiary">
                {template.category} · {template.mode}
              </p>
              <p className="mt-2 text-sm text-text-dark-secondary">{template.description}</p>
            </button>
          ))}
        </div>
      </div>

      {selectedTemplate.mode === 'simple' ? (
        <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-text-dark-tertiary">Essai simple</p>
              <h3 className="mt-1 text-xl font-semibold">{selectedTemplate.label}</h3>
            </div>
            <div className={`rounded-full border px-3 py-1 text-xs uppercase tracking-[0.16em] ${tierTone(selectedTemplate.tier)}`}>
              {tierLabel(selectedTemplate.tier)}
            </div>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <label className="space-y-2 text-sm">
              <span className="block text-text-dark-secondary">Prix / jour</span>
              <input
                className="field-input"
                type="number"
                min="0"
                value={simpleDraft.tjm}
                onChange={(event) => setSimpleDraft((current) => ({ ...current, tjm: Number(event.target.value || 0) }))}
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="block text-text-dark-secondary">Jours</span>
              <input
                className="field-input"
                type="number"
                min="0"
                value={simpleDraft.days}
                onChange={(event) => setSimpleDraft((current) => ({ ...current, days: Number(event.target.value || 0) }))}
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="block text-text-dark-secondary">Frais fixes</span>
              <input
                className="field-input"
                type="number"
                min="0"
                value={simpleDraft.fixedCosts}
                onChange={(event) => setSimpleDraft((current) => ({ ...current, fixedCosts: Number(event.target.value || 0) }))}
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="block text-text-dark-secondary">Frais variables</span>
              <input
                className="field-input"
                type="number"
                min="0"
                value={simpleDraft.variableCosts}
                onChange={(event) => setSimpleDraft((current) => ({ ...current, variableCosts: Number(event.target.value || 0) }))}
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="block text-text-dark-secondary">Marge de sécurité %</span>
              <input
                className="field-input"
                type="number"
                min="0"
                value={simpleDraft.contingencyPct}
                onChange={(event) => setSimpleDraft((current) => ({ ...current, contingencyPct: Number(event.target.value || 0) }))}
              />
            </label>
            <label className="space-y-2 text-sm">
              <span className="block text-text-dark-secondary">Net visé</span>
              <input
                className="field-input"
                type="number"
                min="0"
                value={simpleDraft.netTarget}
                onChange={(event) => setSimpleDraft((current) => ({ ...current, netTarget: Number(event.target.value || 0) }))}
              />
            </label>
          </div>

          {simpleResult && (
            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {[
                ['Budget', formatCurrency(simpleResult.revenue)],
                ['Couts', formatCurrency(simpleResult.totalCosts)],
                ['Reste', formatCurrency(simpleResult.grossMargin)],
                ['Prix mini / jour', formatCurrency(simpleResult.targetTjm)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-border-strong bg-background-dark-tertiary p-4">
                  <p className="text-xs uppercase tracking-[0.16em] text-text-dark-tertiary">{label}</p>
                  <p className="mt-2 text-2xl font-semibold text-primary-300">{value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-text-dark-tertiary">Scratchpad</p>
              <h3 className="mt-1 text-xl font-semibold">{selectedTemplate.label}</h3>
            </div>
            <div className={`rounded-full border px-3 py-1 text-xs uppercase tracking-[0.16em] ${tierTone(selectedTemplate.tier)}`}>
              {tierLabel(selectedTemplate.tier)}
            </div>
          </div>

          <label className="mt-5 block space-y-2 text-sm">
            <span className="block text-text-dark-secondary">Essai modifiable</span>
            <textarea
              className="field-input min-h-[220px] font-mono text-sm"
              value={scratchpad}
              onChange={(event) => setScratchpad(event.target.value)}
            />
          </label>

          {scratchpadResult && (
            <div className="mt-5 space-y-3">
              {scratchpadResult.lines.map((line) => (
                <div
                  key={`${line.label}-${line.raw}`}
                  className={`rounded-xl border px-4 py-3 ${line.kind === 'value' ? 'border-emerald-500/30 bg-emerald-300/8' : 'border-rose-500/30 bg-rose-300/8'}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="font-semibold">{line.label}</p>
                    <p className="text-sm text-text-dark-secondary">{line.formattedValue || line.message}</p>
                  </div>
                  <p className="mt-1 text-xs uppercase tracking-[0.14em] text-text-dark-tertiary">{line.raw}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <a href={STRIPE_STARTER} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-full text-center">
          Activer Starter
        </a>
        <a href={STRIPE_PRO} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full text-center">
          Passer en Pro
        </a>
        <Link to="/accueil" className="btn btn-secondary btn-full text-center">
          Retour accueil
        </Link>
      </div>
    </div>
  );
}

export function MissioPreviewDemo() {
  const profile = useProfile();
  const projects = useActiveProjects();
  const currentProject = useCurrentProject();
  const sessions = useCalcMissionSessions();
  const dealRooms = useWorkspaceStore((state) => state.dealRooms);
  const cras = useWorkspaceStore((state) => state.cras);
  const treasuries = useWorkspaceStore((state) => state.treasuries);
  const moscows = useWorkspaceStore((state) => state.moscows);
  const [stageFocus, setStageFocus] = useState<'all' | 'prospect' | 'gagne' | 'en-cours' | 'facture'>('all');
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | 'brouillon' | 'envoyee' | 'payee'>('all');
  const [selectedMissionId, setSelectedMissionId] = useState<string | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [invoices, setInvoices] = useState(() => readMissioInvoices());

  const currentCalcSession = useMemo(
    () => sessions.find((session) => session.projectId === currentProject?.id) || sessions[0] || null,
    [currentProject?.id, sessions],
  );

  const workspace = useMemo(() => {
    const state = useWorkspaceStore.getState();
    return {
      ...state,
      projects,
      dealRooms,
      cras,
      treasuries,
      moscows,
    } as Workspace;
  }, [cras, dealRooms, moscows, projects, treasuries]);

  const pipeline = useMemo(() => {
    const preview = buildMissioPipelinePreview(workspace, currentCalcSession);
    if (preview.missions.length) return preview;
    return buildFallbackMissioPreview(profile);
  }, [currentCalcSession, profile, workspace]);

  const visibleMissions = useMemo(() => {
    if (stageFocus === 'all') return pipeline.missions;
    return pipeline.grouped[stageFocus];
  }, [pipeline, stageFocus]);

  const selectedMission = useMemo(() => {
    if (!visibleMissions.length) return null;
    return visibleMissions.find((mission) => mission.id === selectedMissionId) || visibleMissions[0] || null;
  }, [selectedMissionId, visibleMissions]);

  const selectedMissionInvoices = useMemo(
    () => invoices.filter((invoice) => invoice.missionId === selectedMission?.id),
    [invoices, selectedMission?.id],
  );

  const visibleInvoices = useMemo(() => {
    if (invoiceFilter === 'all') return invoices;
    return invoices.filter((invoice) => normalizeInvoiceStatus(invoice.status) === invoiceFilter);
  }, [invoiceFilter, invoices]);

  useEffect(() => {
    if (!visibleMissions.length) {
      setSelectedMissionId(null);
      return;
    }
    if (!selectedMissionId || !visibleMissions.some((mission) => mission.id === selectedMissionId)) {
      setSelectedMissionId(visibleMissions[0].id);
    }
  }, [selectedMissionId, visibleMissions]);

  useEffect(() => {
    const refreshInvoices = () => setInvoices(readMissioInvoices());
    refreshInvoices();
    window.addEventListener('focus', refreshInvoices);
    window.addEventListener('storage', refreshInvoices);
    return () => {
      window.removeEventListener('focus', refreshInvoices);
      window.removeEventListener('storage', refreshInvoices);
    };
  }, []);

  const counts = pipeline.counts;

  return (
    <div className="missio-preview">
      <section className="missio-preview-intro">
        <p className="missio-preview-kicker">Démo du cockpit</p>
        <h3 className="missio-preview-title">Missio</h3>
        <p className="missio-preview-copy">
          Visualisez comment passer d’une opportunité à une mission suivie, puis à une facture.
        </p>
        <div className="missio-preview-filters" role="group" aria-label="Filtrer les missions par étape">
          {(['all', 'prospect', 'gagne', 'en-cours', 'facture'] as const).map((stage) => (
            <button
              key={stage}
              type="button"
              className={`missio-preview-filter${stageFocus === stage ? ' is-active' : ''}`}
              onClick={() => setStageFocus(stage)}
              aria-pressed={stageFocus === stage}
            >
              {getMissioStageLabel(stage)}
            </button>
          ))}
        </div>
        <div className="missio-preview-stats">
          {([
            ['À explorer', counts.prospect],
            ['Cadrées', counts.gagne],
            ['En cours', counts['en-cours']],
            ['Facturées', counts.facture],
          ] as const).map(([label, value]) => (
            <div key={label} className="missio-preview-stat">
              <p className="missio-preview-label">{label}</p>
              <p className="missio-preview-stat-value">{value}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="missio-preview-grid">
        <section className="missio-preview-section">
          <div className="missio-preview-section-head">
            <p className="missio-preview-label">Missions visibles</p>
            <span className="missio-preview-count">{visibleMissions.length} mission{visibleMissions.length > 1 ? 's' : ''}</span>
          </div>

          <div className="missio-preview-list">
            {visibleMissions.length ? visibleMissions.map((mission) => (
              <button
                key={mission.id}
                type="button"
                className={`missio-preview-mission${selectedMission?.id === mission.id ? ' is-selected' : ''}`}
                onClick={() => setSelectedMissionId(mission.id)}
                aria-pressed={selectedMission?.id === mission.id}
              >
                <div className="missio-preview-row">
                  <div>
                    <p className="missio-preview-name">{mission.titre}</p>
                    <p className="missio-preview-client">{mission.clientNom || 'Client à préciser'}</p>
                  </div>
                  <span className="missio-preview-status">{getMissioStageLabel(mission.statut)}</span>
                </div>
                <div className="missio-preview-meta">
                  <span>{formatCurrencyShort(mission.budgetHT)}</span>
                  <span>{mission.joursEstimes || 0} jour{mission.joursEstimes === 1 ? '' : 's'}</span>
                  <span>TJM {formatCurrencyShort(mission.tjm || 0)}</span>
                </div>
              </button>
            )) : (
              <p className="missio-preview-empty">Aucune mission dans cette étape.</p>
            )}
          </div>
        </section>

        <div className="missio-preview-side">
          <section className="missio-preview-section">
            <div className="missio-preview-section-head">
              <div>
                <p className="missio-preview-label">Mission sélectionnée</p>
                <h4 className="missio-preview-title">{selectedMission?.titre || 'Aucune mission'}</h4>
              </div>
            </div>

            {selectedMission ? (
              <div className="missio-preview-detail-grid">
                <div className="missio-preview-detail">
                  <p className="missio-preview-label">Client</p>
                  <p className="missio-preview-detail-value">{selectedMission.clientNom || 'À préciser'}</p>
                </div>
                <div className="missio-preview-detail">
                  <p className="missio-preview-label">Budget</p>
                  <p className="missio-preview-detail-value">{formatCurrency(selectedMission.budgetHT)}</p>
                </div>
                <div className="missio-preview-detail">
                  <p className="missio-preview-label">État</p>
                  <p className="missio-preview-detail-value">{getMissioStageLabel(selectedMission.statut)}</p>
                </div>
                <div className="missio-preview-detail">
                  <p className="missio-preview-label">Durée estimée</p>
                  <p className="missio-preview-detail-value">{selectedMission.joursEstimes || 0} jour{selectedMission.joursEstimes === 1 ? '' : 's'}</p>
                </div>
              </div>
            ) : (
              <p className="missio-preview-empty">Sélectionnez une mission pour afficher ses repères.</p>
            )}

            <p className="missio-preview-note">
              Le cockpit complet relie pipeline, suivi de mission et facturation au même endroit.
            </p>
          </section>

          <section className="missio-preview-section">
            <div className="missio-preview-section-head">
              <p className="missio-preview-label">Factures</p>
              <div className="missio-preview-filters" style={{ marginTop: 0 }} role="group" aria-label="Filtrer les factures">
                {(['all', 'brouillon', 'envoyee', 'payee'] as const).map((filter) => (
                  <button
                    key={filter}
                    type="button"
                    className={`missio-preview-filter${invoiceFilter === filter ? ' is-active' : ''}`}
                    onClick={() => setInvoiceFilter(filter)}
                    aria-pressed={invoiceFilter === filter}
                  >
                    {getMissioInvoiceLabel(filter)}
                  </button>
                ))}
              </div>
            </div>

            <div className="missio-preview-list">
              {visibleInvoices.length ? visibleInvoices.map((invoice) => {
                const invoiceStatus = normalizeInvoiceStatus(invoice.status);
                return (
                  <button
                    key={invoice.id}
                    type="button"
                    className={`missio-preview-invoice${selectedInvoiceId === invoice.id ? ' is-selected' : ''}`}
                    onClick={() => setSelectedInvoiceId(invoice.id)}
                    aria-pressed={selectedInvoiceId === invoice.id}
                  >
                    <div className="missio-preview-row">
                      <div>
                        <p className="missio-preview-name">{invoice.number}</p>
                        <p className="missio-preview-client">{invoice.title || invoice.missionTitle || 'Facture de démonstration'}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p className="missio-preview-name">{invoice.amount ? formatCurrency(invoice.amount) : '—'}</p>
                        <p className="missio-preview-client">{getMissioInvoiceLabel(invoiceStatus)}</p>
                      </div>
                    </div>
                  </button>
                );
              }) : (
                <p className="missio-preview-empty">Aucune facture dans cette catégorie.</p>
              )}
            </div>

            {selectedMissionInvoices.length > 0 && (
              <p className="missio-preview-note">
                {selectedMissionInvoices.length} facture{selectedMissionInvoices.length > 1 ? 's' : ''} liée{selectedMissionInvoices.length > 1 ? 's' : ''} à cette mission.
              </p>
            )}
          </section>
        </div>
      </div>

      <p className="missio-preview-footnote">Cette démonstration utilise des données d’exemple. Vos données restent locales tant que vous ne les partagez pas.</p>
    </div>
  );
}

export function DevisPreviewDemo() {
  const profile = useProfile();
  const tjm = profile.tjm || 650;
  const [shared, setShared] = useState(false);

  const lines = [
    { desc: 'Architecture technique — socle produit', qty: 8, pu: tjm },
    { desc: 'Implémentation & tests', qty: 14, pu: tjm },
    { desc: 'Livraison & documentation', qty: 3, pu: tjm },
  ];
  const totalHT = lines.reduce((s, l) => s + l.qty * l.pu, 0);
  const tva = 20;
  const totalTTC = Math.round(totalHT * (1 + tva / 100));

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-text-dark-tertiary">Démo devis</p>
            <h3 className="mt-1 text-xl font-semibold">Aperçu document</h3>
          </div>
          <span className="rounded-full border border-amber-400/40 bg-amber-300/10 px-3 py-1 text-xs uppercase tracking-[0.16em] text-amber-100">Brouillon</span>
        </div>

        <div className="mt-4 space-y-1">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 90px 90px', gap: 8, padding: '0 4px' }}>
            {['Description', 'Qté', 'P.U.', 'Total'].map((h) => (
              <span key={h} className="text-xs uppercase tracking-[0.14em] text-text-dark-tertiary" style={{ textAlign: h !== 'Description' ? 'right' : undefined }}>{h}</span>
            ))}
          </div>
          {lines.map((line, i) => (
            <div key={i} className="rounded-xl border border-border-strong bg-background-dark-tertiary px-3 py-2" style={{ display: 'grid', gridTemplateColumns: '1fr 40px 90px 90px', gap: 8, alignItems: 'center' }}>
              <span className="text-sm text-text-dark-secondary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{line.desc}</span>
              <span className="text-xs text-text-dark-tertiary" style={{ textAlign: 'right' }}>{line.qty}j</span>
              <span className="text-xs text-text-dark-tertiary" style={{ textAlign: 'right' }}>{line.pu.toLocaleString('fr-FR')} €</span>
              <span className="text-sm font-semibold" style={{ textAlign: 'right' }}>{(line.qty * line.pu).toLocaleString('fr-FR')} €</span>
            </div>
          ))}
        </div>

        <div className="mt-3 rounded-xl border border-border-strong bg-background-dark-tertiary px-4 py-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-text-dark-secondary">Total HT</span>
            <span className="font-semibold">{totalHT.toLocaleString('fr-FR')} €</span>
          </div>
          <div className="flex justify-between text-xs text-text-dark-tertiary">
            <span>TVA {tva}%</span>
            <span>{(totalHT * tva / 100).toLocaleString('fr-FR')} €</span>
          </div>
          <div className="flex justify-between border-t border-border-strong pt-2 text-base font-bold">
            <span>Total TTC</span>
            <span style={{ color: '#eb3000' }}>{totalTTC.toLocaleString('fr-FR')} €</span>
          </div>
        </div>

        <button
          className="mt-4 w-full rounded-xl border border-border-strong bg-background-dark-tertiary px-4 py-2 text-sm text-text-dark-secondary"
          onClick={() => setShared(true)}
        >
          {shared ? '✓ Lien prêt — partagez avec votre client' : '↗ Partager ce devis (démo)'}
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <a href={STRIPE_STARTER} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-full text-center">Activer Starter</a>
        <a href={STRIPE_PRO} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full text-center">Passer en Pro</a>
        <Link to="/accueil" className="btn btn-secondary btn-full text-center">Retour accueil</Link>
      </div>
    </div>
  );
}

export function CraPreviewDemo() {
  const profile = useProfile();
  const tjm = profile.tjm || 600;
  const today = useMemo(() => new Date(), []);
  const year = today.getFullYear();
  const month = today.getMonth();
  const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7;

  const CYCLE = [null, 'travaille', 'demi', 'conge'] as const;
  type DayState = typeof CYCLE[number];

  const [states, setStates] = useState<Record<number, DayState>>(() => {
    const s: Record<number, DayState> = {};
    for (let d = 1; d <= Math.min(daysInMonth, today.getDate()); d++) {
      const dow = new Date(year, month, d).getDay();
      if (dow >= 1 && dow <= 5) s[d] = 'travaille';
    }
    return s;
  });

  function toggle(day: number) {
    setStates((prev) => {
      const cur = prev[day] ?? null;
      const idx = CYCLE.indexOf(cur);
      const next = CYCLE[(idx + 1) % CYCLE.length];
      return { ...prev, [day]: next };
    });
  }

  const worked = Object.values(states).reduce((s, v) => s + (v === 'travaille' ? 1 : v === 'demi' ? 0.5 : 0), 0);
  const leaves = Object.values(states).filter((v) => v === 'conge').length;
  const revenue = Math.round(worked * tjm);

  function dayStyle(state: DayState, isWeekend: boolean): React.CSSProperties {
    if (isWeekend) return { opacity: 0.25, cursor: 'default' };
    const base: React.CSSProperties = { padding: '5px 2px', borderRadius: 6, fontSize: 11, border: 'none', cursor: 'pointer', fontWeight: 500 };
    if (state === 'travaille') return { ...base, background: 'var(--ix-positive-subtle)', color: 'var(--ix-positive)', fontWeight: 700 };
    if (state === 'demi') return { ...base, background: 'rgba(251,146,60,0.15)', color: '#fb923c', fontWeight: 700 };
    if (state === 'conge') return { ...base, background: 'rgba(129,140,248,0.15)', color: '#818cf8', fontWeight: 700 };
    return { ...base, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.45)' };
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-text-dark-tertiary">Démo calendrier</p>
            <h3 className="mt-1 text-xl font-semibold">{MONTH_NAMES[month]} {year}</h3>
          </div>
          <div className="flex gap-3 text-xs">
            <span style={{ color: 'var(--ix-positive)' }}>■ Travaillé</span>
            <span style={{ color: '#fb923c' }}>■ ½</span>
            <span style={{ color: '#818cf8' }}>■ Congé</span>
          </div>
        </div>

        <div className="mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
            <div key={d} className="text-center text-xs text-text-dark-tertiary py-1">{d}</div>
          ))}
          {Array.from({ length: firstDow }, (_, i) => <div key={`off-${i}`} />)}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const d = i + 1;
            const dow = new Date(year, month, d).getDay();
            const isWeekend = dow === 0 || dow === 6;
            return (
              <button
                key={d}
                disabled={isWeekend}
                onClick={() => toggle(d)}
                style={{ ...dayStyle(states[d] ?? null, isWeekend), textAlign: 'center', width: '100%' }}
              >
                {d}
              </button>
            );
          })}
        </div>

        <div className="mt-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[
            { label: 'Jours travaillés', value: `${worked}j` },
            { label: 'Congés', value: `${leaves}j` },
            { label: 'CA mois', value: `${revenue.toLocaleString('fr-FR')} €` },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-xl border border-border-strong bg-background-dark-tertiary px-3 py-3">
              <p className="text-xs uppercase tracking-[0.12em] text-text-dark-tertiary">{label}</p>
              <p className="mt-1 text-base font-semibold">{value}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-text-dark-tertiary">Cliquez sur un jour pour basculer l'état : travaillé · ½ journée · congé.</p>
      </div>

      <div className="flex flex-wrap gap-3">
        <a href={STRIPE_STARTER} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-full text-center">Activer Starter</a>
        <a href={STRIPE_PRO} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full text-center">Passer en Pro</a>
        <Link to="/accueil" className="btn btn-secondary btn-full text-center">Retour accueil</Link>
      </div>
    </div>
  );
}

export function TresoreriePreviewDemo() {
  const profile = useProfile();
  const tjm = profile.tjm || 600;
  const daysPerMonth = profile.daysPerMonth || 15;
  const revenue = tjm * daysPerMonth;
  const charges = Math.round(revenue * 0.38);

  const [balance, setBalance] = useState(() => Math.round(revenue * 2));

  const scenarios = useMemo(() => [
    { label: 'Prudent', revenue: revenue * 0.75, charges: charges * 1.05 },
    { label: 'Normal', revenue, charges },
    { label: 'Actif', revenue: revenue * 1.15, charges },
  ].map((s) => {
    const margin = s.revenue - s.charges;
    const months = margin > 0 ? Math.min(balance / margin, 24) : 0;
    return { ...s, margin, months };
  }), [balance, charges, revenue]);

  function fmtMois(v: number) {
    if (v < 1) return '< 1 mois';
    if (v >= 24) return '24+ mois';
    return `${v.toFixed(1)} mois`;
  }

  function tone(months: number) {
    return months >= 9 ? 'var(--ix-positive)' : months >= 6 ? 'var(--ix-amber)' : months >= 3 ? 'var(--ix-orange)' : 'var(--ix-negative)';
  }

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-text-dark-tertiary">Démo trésorerie</p>
          <h3 className="mt-1 text-xl font-semibold">Scénarios de réserve</h3>
        </div>

        <div className="sim-slider mt-5">
          <div className="sim-slider-header">
            <span className="sim-slider-label">Trésorerie initiale</span>
            <span className="sim-slider-value">{balance.toLocaleString('fr-FR')} €</span>
          </div>
          <input type="range" min={0} max={Math.round(revenue * 12)} step={500} value={balance} onChange={(e) => setBalance(Number(e.target.value))} />
        </div>

        <div className="mt-5 space-y-3">
          {scenarios.map((s) => {
            const c = tone(s.months);
            const pct = Math.min(100, (s.months / 24) * 100);
            return (
              <div key={s.label} className="rounded-xl border border-border-strong bg-background-dark-tertiary px-4 py-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{s.label}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: c }}>{fmtMois(s.months)}</span>
                </div>
                <div className="mt-2" style={{ height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.06)' }}>
                  <div style={{ width: `${pct}%`, height: 4, borderRadius: 99, background: c, transition: 'width 0.3s' }} />
                </div>
                <div className="mt-1 text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Marge {s.margin > 0 ? '+' : ''}{Math.round(s.margin).toLocaleString('fr-FR')} €/mois
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <a href={STRIPE_STARTER} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-full text-center">Activer Starter</a>
        <a href={STRIPE_PRO} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full text-center">Passer en Pro</a>
        <Link to="/accueil" className="btn btn-secondary btn-full text-center">Retour accueil</Link>
      </div>
    </div>
  );
}

export function RunwayPreviewDemo() {
  const profile = useProfile();
  const [tjm, setTjm] = useState(profile.tjm || 600);
  const daysPerMonth = profile.daysPerMonth || 15;
  const revenue = tjm * daysPerMonth;
  const charges = Math.round(revenue * 0.4);
  const [balance, setBalance] = useState(() => Math.round((profile.tjm || 600) * (profile.daysPerMonth || 15) * 2));

  const margin = revenue - charges;
  const runwayMonths = margin > 0 && balance > 0 ? Math.min(balance / margin, 24) : margin <= 0 && charges > 0 ? 0 : 0;

  const scenarios = useMemo(() => [
    { label: 'Prudent', rev: revenue * 0.75, chg: charges * 1.05 },
    { label: 'Normal', rev: revenue, chg: charges },
    { label: 'Actif', rev: revenue * 1.15, chg: charges },
  ].map((s) => {
    const m = s.rev - s.chg;
    const months = m > 0 && balance > 0 ? Math.min(balance / m, 24) : 0;
    return { ...s, months };
  }), [balance, charges, revenue]);

  function fmtMois(v: number) {
    if (v < 1) return '< 1 mois';
    if (v >= 24) return '24+ mois';
    return `${v.toFixed(1)} mois`;
  }

  function tone(months: number) {
    return months >= 9 ? 'var(--ix-positive)' : months >= 6 ? 'var(--ix-amber)' : months >= 3 ? 'var(--ix-orange)' : 'var(--ix-negative)';
  }

  function zoneInfo(months: number) {
    if (months >= 12) return { label: 'Très solide', emoji: '🛡️' };
    if (months >= 6) return { label: 'Stable', emoji: '⚖️' };
    if (months >= 3) return { label: 'Tendu', emoji: '⚠️' };
    if (months > 0) return { label: 'Critique', emoji: '🔴' };
    return { label: 'Rupture', emoji: '💥' };
  }

  const zoneColor = tone(runwayMonths);
  const zone = zoneInfo(runwayMonths);
  const gaugePct = Math.min(100, (runwayMonths / 12) * 100);

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-border-strong bg-background-dark-secondary p-5">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-text-dark-tertiary">Démo réserve</p>
          <h3 className="mt-1 text-xl font-semibold">Runway freelance</h3>
        </div>

        <div className="mt-4 flex items-center gap-4 rounded-xl px-4 py-3" style={{ background: `${zoneColor}12`, border: `1px solid ${zoneColor}28` }}>
          <span style={{ fontSize: 22 }}>{zone.emoji}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: zoneColor }}>{zone.label}</div>
            <div style={{ fontSize: 11, color: zoneColor, opacity: 0.75 }}>{fmtMois(runwayMonths)} de réserve</div>
          </div>
          <div className="ml-auto" style={{ fontSize: 22, fontWeight: 800, color: zoneColor }}>{fmtMois(runwayMonths)}</div>
        </div>

        <div className="mt-3" style={{ height: 6, borderRadius: 99, background: 'rgba(255,255,255,0.06)' }}>
          <div style={{ width: `${gaugePct}%`, height: 6, borderRadius: 99, background: zoneColor, transition: 'all 0.3s' }} />
        </div>
        <div className="mt-1 flex justify-between text-xs text-text-dark-tertiary">
          <span>0</span><span>6 mois</span><span>12 mois</span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="sim-slider">
            <div className="sim-slider-header">
              <span className="sim-slider-label">TJM</span>
              <span className="sim-slider-value">{tjm} €</span>
            </div>
            <input type="range" min={200} max={2000} step={10} value={tjm} onChange={(e) => setTjm(Number(e.target.value))} />
          </div>
          <div className="sim-slider">
            <div className="sim-slider-header">
              <span className="sim-slider-label">Réserve</span>
              <span className="sim-slider-value">{Math.round(balance / 1000)}k €</span>
            </div>
            <input type="range" min={0} max={100000} step={500} value={balance} onChange={(e) => setBalance(Number(e.target.value))} />
          </div>
        </div>

        <div className="mt-4 space-y-2">
          {scenarios.map((s) => {
            const c = tone(s.months);
            const pct = Math.min(100, (s.months / 12) * 100);
            return (
              <div key={s.label} className="flex items-center gap-3">
                <span className="w-14 text-xs text-text-dark-tertiary">{s.label}</span>
                <div className="flex-1" style={{ height: 4, borderRadius: 99, background: 'rgba(255,255,255,0.06)' }}>
                  <div style={{ width: `${pct}%`, height: 4, borderRadius: 99, background: c, transition: 'width 0.3s' }} />
                </div>
                <span className="w-16 text-right text-xs" style={{ color: c }}>{fmtMois(s.months)}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <a href={STRIPE_STARTER} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-full text-center">Activer Starter</a>
        <a href={STRIPE_PRO} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-full text-center">Passer en Pro</a>
        <Link to="/accueil" className="btn btn-secondary btn-full text-center">Retour accueil</Link>
      </div>
    </div>
  );
}

function normalizeInvoiceStatus(status: string) {
  const normalized = status.toLowerCase();
  if (normalized.includes('pay')) return 'payee';
  if (normalized.includes('env')) return 'envoyee';
  return 'brouillon';
}
